import {
  useCallback,
  useLayoutEffect,
  useRef,
  FocusEvent as ReactFocusEvent
} from 'react';
import {
  useFocusWithin as useAriaFocusWithin,
  FocusWithinProps,
  FocusWithinResult
} from 'react-aria';

export type { FocusWithinProps, FocusWithinResult };

/**
 * Custom useFocusWithin that wraps react-aria's useFocusWithin but delays
 * onBlurWithin when a nested element has opened a popover/dropdown via portal.
 *
 * If an element within the container has aria-expanded=true, the blur event
 * is delayed until aria-expanded becomes false, preventing premature blur
 * when focus moves to portaled content.
 */
export function useFocusWithin(props: FocusWithinProps): FocusWithinResult {
  const { onBlurWithin, onFocusWithin, onFocusWithinChange, isDisabled } =
    props;

  // Deferred blur fires after later renders; always call the latest callback, not the one captured at blur time
  const onBlurWithinRef = useRef(onBlurWithin);
  useLayoutEffect(() => {
    onBlurWithinRef.current = onBlurWithin;
  });

  const pendingBlurEventRef = useRef<ReactFocusEvent | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const containerRef = useRef<EventTarget | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const cleanupPendingBlur = useCallback(() => {
    pendingBlurEventRef.current = null;
    containerRef.current = null;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const hasExpandedElement = useCallback((container: Element): boolean => {
    // Check the container itself or any descendants
    return (
      container.getAttribute('aria-expanded') === 'true' ||
      container.querySelector('[aria-expanded="true"]') !== null
    );
  }, []);

  const checkAndTriggerBlur = useCallback(() => {
    if (!pendingBlurEventRef.current) return;

    const event = pendingBlurEventRef.current;
    const container = containerRef.current;

    if (!(container instanceof Element)) {
      cleanupPendingBlur();
      return;
    }

    // If any element still has aria-expanded=true, popover is still open
    if (hasExpandedElement(container)) {
      return; // Keep waiting
    }

    // Defer the blur trigger to allow focus restoration to complete
    // If focus returns to the container, handleAriaFocus will cancel this timeout
    timeoutRef.current = window.setTimeout(() => {
      // Check if focus is still within the container
      // If it is, don't trigger blur (focus returned to trigger)
      if (
        document.activeElement &&
        container.contains(document.activeElement)
      ) {
        cleanupPendingBlur();
        return; // Focus is still within, do nothing
      }

      cleanupPendingBlur();
      onBlurWithinRef.current?.(event);
    }, 10); // Small delay to allow focus restoration
  }, [cleanupPendingBlur, hasExpandedElement]);

  const handleAriaBlur = useCallback(
    (e: ReactFocusEvent) => {
      const container = e.currentTarget;

      if (!(container instanceof Element)) {
        onBlurWithin?.(e);
        return;
      }

      // Check if any element has aria-expanded=true
      if (hasExpandedElement(container)) {
        // Store the blur event and container reference
        pendingBlurEventRef.current = e;
        containerRef.current = container;

        // Set up a MutationObserver to watch for any aria-expanded changes in the subtree
        observerRef.current = new MutationObserver(() => {
          checkAndTriggerBlur();
        });

        observerRef.current.observe(container, {
          attributes: true,
          attributeFilter: ['aria-expanded'],
          subtree: true // Watch all descendants
        });
      } else {
        // No expanded element, trigger blur immediately
        onBlurWithin?.(e);
      }
    },
    [hasExpandedElement, checkAndTriggerBlur, onBlurWithin]
  );

  const handleAriaFocus = useCallback(
    (e: ReactFocusEvent) => {
      // If there's a pending blur, cancel it since we're focusing back in
      if (pendingBlurEventRef.current) {
        cleanupPendingBlur();
      }

      onFocusWithin?.(e);
    },
    [onFocusWithin, cleanupPendingBlur]
  );

  // Use react-aria's useFocusWithin with our custom handlers
  const { focusWithinProps } = useAriaFocusWithin({
    onFocusWithin: handleAriaFocus,
    onBlurWithin: handleAriaBlur,
    onFocusWithinChange,
    isDisabled
  });

  return { focusWithinProps };
}
